/**
 * Leave Management Page - Manage user leave balances (Admin/HR only)
 */

import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import adminApi from '@/modules/admin/api/adminApi'
import LoadingPage from '@/shared/components/LoadingPage'
import type { LeaveBalance } from '@/modules/admin/types/admin.types'

type SortField = 'full_name' | 'email' | 'division_name'
type SortOrder = 'asc' | 'desc'

const BalancePill = ({ balance }: { balance: LeaveBalance }) => {
  const color = balance.leave_type_color || '#6b7280'
  const isUnlimited = balance.total_allocated === 0

  return (
    <div
      className="inline-flex flex-col items-center px-3 py-1.5 rounded-lg text-xs border"
      style={{ borderColor: `${color}40`, backgroundColor: `${color}10` }}
    >
      <span className="font-medium mb-0.5" style={{ color }}>
        {balance.leave_type_name}
      </span>
      {isUnlimited ? (
        <span className="text-gray-500">{balance.used} used</span>
      ) : (
        <span className="text-gray-500">
          <span className="font-semibold text-gray-800">{balance.available ?? 0}</span>
          {' '}left / {balance.total_allocated}
          {balance.pending > 0 && (
            <span className="ml-1 text-amber-600">({balance.pending} pend.)</span>
          )}
        </span>
      )}
    </div>
  )
}

const LeaveManagementPage = () => {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('full_name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['adminUsers', { search: searchTerm, page }],
    queryFn: () => adminApi.getUsers({ search: searchTerm, page, limit: 20 }),
    placeholderData: keepPreviousData,
  })

  const users = data?.data || []
  const pagination = data?.pagination

  // Sort users
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal: string = ((a[sortField] as string | null) || '').toLowerCase()
      const bVal: string = ((b[sortField] as string | null) || '').toLowerCase()
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [users, sortField, sortOrder])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getAvatarGradient = (name: string) => {
    const gradients = [
      'from-violet-500 to-purple-600',
      'from-blue-500 to-cyan-500',
      'from-emerald-500 to-teal-500',
      'from-orange-500 to-amber-500',
      'from-pink-500 to-rose-500',
      'from-indigo-500 to-blue-500',
    ]
    const index = name.charCodeAt(0) % gradients.length
    return gradients[index]
  }

  if (isLoading) return <LoadingPage />

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/leave" className="hover:text-white transition-colors">Leave</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">All Employees Balances</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">All Employees Balances</h1>
              <p className="text-red-200">Manage employee leave allocations and track balances</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/leave/holidays"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Public Holidays
              </Link>
              <Link
                to="/leave/types"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Leave Types
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6">
        {/* Search and Sort Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search employees by name, email, or department..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-12 pr-12 py-3 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white transition-all text-gray-900 placeholder-gray-400"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">Sort by:</span>
              <button
                onClick={() => handleSort('full_name')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortField === 'full_name' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Name {sortField === 'full_name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('division_name')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  sortField === 'division_name' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Dept {sortField === 'division_name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
            {isFetching && !isLoading && (
              <span className="text-xs text-gray-400 whitespace-nowrap">Updating…</span>
            )}
          </div>
        </div>

        {/* Employee Cards */}
        <div className="space-y-3 mb-8">
          {sortedUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Employee info */}
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getAvatarGradient(user.full_name || '')} flex items-center justify-center text-white font-semibold text-sm shadow flex-shrink-0`}>
                    {getInitials(user.full_name || '')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-0.5">
                      <h3 className="font-semibold text-gray-900">{user.full_name}</h3>
                      {user.division_name && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                          {user.division_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mb-3">{user.email}</p>

                    {/* Leave balance pills */}
                    {(user.leave_balances ?? []).length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {(user.leave_balances ?? []).map((balance) => (
                          <BalancePill key={balance.leave_type_id} balance={balance} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No leave balances for {new Date().getFullYear()}</p>
                    )}
                  </div>
                </div>

                {/* Edit Button */}
                <button
                  onClick={() => navigate(`/leave/manage/${user.id}`)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 transition-colors flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {sortedUsers.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-500">Try adjusting your search criteria</p>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{(page - 1) * 20 + 1}</span> to{' '}
              <span className="font-semibold text-gray-900">{Math.min(page * 20, pagination.total)}</span> of{' '}
              <span className="font-semibold text-gray-900">{pagination.total}</span> employees
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <div className="px-4 py-2 rounded-lg bg-red-50 text-red-600 font-semibold">
                {page}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              <button
                onClick={() => setPage(pagination.pages)}
                disabled={page >= pagination.pages}
                className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LeaveManagementPage
